import { useEffect, useRef, useState } from 'react';
import { send } from '@/shared/messages';
import { SCENARIO_PRESETS, gradeScenario } from '@/shared/constants';
import type { Scenario } from '@/shared/types';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Alert } from '@/components/Alert';
import { Input, Checkbox } from '@/components/Field';
import { Markdown } from '../components/Markdown';

// Scenario roleplay: on-demand decision-making practice scoped to the active
// test's domain. The model generates a situation + ranked goals + 4 strategy
// options (optionally grounded via web search); the learner picks one and the
// best choice is revealed with per-option rationale. Attempts are recorded.
export function ScenarioRoute({ activeTestId }: { activeTestId: string | null }) {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [active, setActive] = useState<Scenario | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);

  const [preset, setPreset] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [webSearch, setWebSearch] = useState(true);

  const [pending, setPending] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Per-generation id so the streaming listener only reacts to our own run.
  const requestIdRef = useRef<string>('');

  // Load past scenarios for the active test.
  useEffect(() => {
    if (!activeTestId) {
      setScenarios([]);
      return;
    }
    let cancelled = false;
    void send({ type: 'LIST_SCENARIOS', testId: activeTestId })
      .then((r) => {
        if (!cancelled) setScenarios(r.scenarios);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [activeTestId]);

  // Live web-search / progress while generating, filtered to our requestId.
  useEffect(() => {
    function onMessage(m: unknown) {
      if (!m || typeof m !== 'object' || !('type' in m)) return;
      if ((m as { type: string }).type !== 'LLM_DELTA') return;
      const d = m as { surface?: string; requestId?: string; text?: string };
      if (d.surface !== 'scenario' || d.requestId !== requestIdRef.current) return;
      setProgress(d.text ?? '');
    }
    chrome.runtime.onMessage.addListener(onMessage);
    return () => chrome.runtime.onMessage.removeListener(onMessage);
  }, []);

  async function generate() {
    if (!activeTestId || pending) return;
    if (!preset && !prompt.trim()) {
      setError('Pick a preset or describe what to practice.');
      return;
    }
    const requestId = crypto.randomUUID();
    requestIdRef.current = requestId;
    setPending(true);
    setProgress('');
    setError(null);
    setActive(null);
    setSelected(null);
    setRevealed(false);
    try {
      const r = await send({
        type: 'GENERATE_SCENARIO',
        testId: activeTestId,
        prompt: preset ? undefined : prompt.trim(),
        preset: preset ?? undefined,
        webSearch,
        requestId,
      });
      setActive(r.scenario);
      setScenarios((s) => [r.scenario, ...s]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
      setProgress('');
    }
  }

  async function pick(i: number) {
    if (!active || revealed) return;
    setSelected(i);
    setRevealed(true);
    try {
      await send({ type: 'ANSWER_SCENARIO', scenarioId: active.id, selectedOption: i });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function openPast(s: Scenario) {
    setActive(s);
    setSelected(null);
    setRevealed(false);
    setError(null);
  }

  async function remove(id: string) {
    try {
      await send({ type: 'DELETE_SCENARIO', scenarioId: id });
      setScenarios((s) => s.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (!activeTestId) {
    return (
      <div className="p-4 text-sm text-slate-500">
        Pick an active test first — scenarios are generated from its material.
      </div>
    );
  }

  const outcome = active && selected !== null ? gradeScenario(active.options, selected) : null;

  return (
    <div className="space-y-4 p-4">
      {/* Generator */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {SCENARIO_PRESETS.map((p) => (
            <Button
              key={p.id}
              size="xs"
              variant={preset === p.id ? 'primary' : 'secondary'}
              onClick={() => {
                setPreset((cur) => (cur === p.id ? null : p.id));
                setPrompt('');
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <Input
          label="Or describe what to practice"
          placeholder="e.g. a retiree rolling over a 401(k) with conflicting income needs"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            if (e.target.value) setPreset(null);
          }}
        />
        <div className="flex items-center justify-between gap-2">
          <Checkbox
            label="Allow web search"
            description="Let the model search to ground the scenario in current specifics."
            checked={webSearch}
            onChange={(e) => setWebSearch(e.target.checked)}
          />
          <Button variant="primary" loading={pending} onClick={() => void generate()}>
            Generate
          </Button>
        </div>
      </div>

      {error && <Alert tone="error">{error}</Alert>}

      {pending && (
        <Card padding="sm" elevation="flat" className="bg-slate-50">
          {progress ? (
            <Markdown source={progress} trailingCaret />
          ) : (
            <span className="animate-pulse text-sm text-slate-500">Building scenario…</span>
          )}
        </Card>
      )}

      {/* Active scenario */}
      {active && !pending && (
        <Card padding="md" className="space-y-3">
          <p className="text-sm leading-relaxed text-slate-800">{active.situation}</p>
          {active.goals.length > 0 && (
            <div className="text-xs text-slate-600">
              <span className="font-medium text-slate-700">Goals (ranked): </span>
              {[...active.goals]
                .sort((a, b) => a.priority - b.priority)
                .map((g) => g.label)
                .join(' › ')}
            </div>
          )}
          <p className="text-sm font-medium text-slate-900">{active.question}</p>

          <div className="grid gap-2">
            {active.options.map((o, i) => {
              const isPicked = selected === i;
              const isBest = revealed && o.rank === 1;
              return (
                <Card
                  key={i}
                  interactive={!revealed}
                  padding="sm"
                  accent={isBest}
                  onClick={revealed ? undefined : () => void pick(i)}
                  className={
                    revealed && isPicked && o.rank !== 1
                      ? 'border border-red-300 bg-red-50'
                      : undefined
                  }
                >
                  <div className="flex items-start gap-2">
                    <span className="font-mono text-xs text-slate-400">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800">{o.text}</p>
                      {revealed && (
                        <p className="mt-1 text-xs text-slate-500">
                          <span className="font-medium">
                            {o.rank === 1 ? 'Best choice' : `Rank ${o.rank}`}
                            {isPicked ? ' · your pick' : ''}
                          </span>{' '}
                          — {o.rationale}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {revealed && outcome && (
            <Alert tone={outcome.correct ? 'success' : 'warning'}>
              {outcome.correct
                ? 'Correct — that was the best strategy.'
                : `Not the best choice — you picked the rank-${outcome.rank} option. See the rationale on the best choice above.`}
            </Alert>
          )}
        </Card>
      )}

      {/* History */}
      {scenarios.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Past scenarios
          </div>
          {scenarios.map((s) => (
            <div
              key={s.id}
              className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${
                active?.id === s.id ? 'bg-slate-100' : 'hover:bg-slate-50'
              }`}
            >
              <button
                className="min-w-0 flex-1 truncate text-left text-slate-700"
                onClick={() => openPast(s)}
                title={s.question}
              >
                {s.question}
              </button>
              <button
                className="shrink-0 text-slate-400 hover:text-red-600"
                onClick={() => void remove(s.id)}
                title="Delete scenario"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
